import type { ReactNode } from "react";
import type { RetrofitProjectListItem } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { humanise, longDate } from "../lib/format.js";

/**
 * The PAS 2035 projects, so a retrofit project can be reached at all.
 *
 * Deliberately separate from the MCS pipeline board rather than merged into it.
 * They are two lifecycles over two records — a retrofit project runs from
 * assessment to TrustMark lodgement, handover and evaluation, while a job runs
 * to a certificate and a BUS redemption — and a single list would have to
 * invent a shared vocabulary of stages that neither standard uses.
 */
export function RetrofitProjects(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<RetrofitProjectListItem[]>(
    () => client.retrofitProjects(),
    [client],
  );

  return (
    <>
      <div className="page-header">
        <h1>Retrofit projects</h1>
        <p className="lede">
          PAS 2035 work: government-funded retrofit under Warm Homes, ECO and the
          rest, certified to PAS 2030 and lodged to TrustMark rather than to MCS.
        </p>
      </div>

      <Async state={state} what="the retrofit projects" reload={reload}>
        {(projects) =>
          projects.length === 0 ? (
            <p className="empty">
              No retrofit projects. This portal cannot create one — every write
              on a PAS 2035 project is reserved to a role held per project, and
              no route exposes those yet.
            </p>
          ) : (
            <div className="table-scroll">
              <table>
                <caption className="visually-hidden">
                  Retrofit projects, most recently commenced first
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Reference</th>
                    <th scope="col">Stage</th>
                    <th scope="col">Funding</th>
                    <th scope="col">Measures</th>
                    <th scope="col">Commenced</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.id}>
                      <th scope="row">
                        <a href={href.retrofit(project.id)}>
                          {project.reference}
                        </a>
                        {project.job_id ? null : (
                          <>
                            <br />
                            <span className="muted small">no MCS job</span>
                          </>
                        )}
                      </th>
                      <td>{humanise(project.stage)}</td>
                      <td>{humanise(project.funding_scheme)}</td>
                      <td>{project.measure_count}</td>
                      <td>{longDate(project.commenced_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Async>
    </>
  );
}
